/**
 * Scroll restoration feature
 * Handles complex scroll position restoration with Lenis synchronization
 */

import type Lenis from 'lenis'

export interface ScrollRestorationInstance {
  /** Restore scroll position with multiple retry attempts */
  restore: (config: { targetScroll: number; lenisInstance: Lenis | null; onComplete?: () => void }) => void
  /** Scroll to top instantly */
  scrollToTop: (lenisInstance: Lenis | null) => void
}

/**
 * Setup scroll restoration
 * @returns Scroll restoration instance
 */
export function setupScrollRestoration(): ScrollRestorationInstance {
  return {
    restore: (config) => runRestoreWithRetries(config.targetScroll, config.lenisInstance, config.onComplete),
    scrollToTop: (lenisInstance) => applyScrollPosition(0, lenisInstance),
  }
}

function applyScrollPosition(targetScroll: number, lenisInstance: Lenis | null): void {
  window.scrollTo({ top: targetScroll, left: 0, behavior: 'auto' })
  if (lenisInstance) {
    lenisInstance.resize()
    lenisInstance.scrollTo(targetScroll, { immediate: true })
    refreshScrollTrigger()
  }
}

function refreshScrollTrigger(): void {
  if (typeof window === 'undefined') return
  const st = (window as { ScrollTrigger?: { refresh: () => void } }).ScrollTrigger
  st?.refresh()
}

function runRestoreWithRetries(targetScroll: number, lenisInstance: Lenis | null, onComplete?: () => void): void {
  const setScroll = () => applyScrollPosition(targetScroll, lenisInstance)

  requestAnimationFrame(() => {
    setScroll()
    requestAnimationFrame(() => {
      setScroll()
      setTimeout(() => {
        setScroll()
        setTimeout(() => {
          setScroll()
          if (lenisInstance) {
            requestAnimationFrame(() => {
              setScroll()
              requestAnimationFrame(() => {
                const current = window.scrollY
                if (Math.abs(lenisInstance.scroll - current) > 1) {
                  window.scrollTo({ top: targetScroll, left: 0, behavior: 'auto' })
                  lenisInstance.scrollTo(targetScroll, { immediate: true })
                }
                refreshScrollTrigger()
                onComplete?.()
              })
            })
          } else {
            onComplete?.()
          }
        }, 100)
      }, 100)
    })
  })
}
